import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpService from "@shared/services/http.service";
import { API_CONFIG } from "@shared/constants/api";
import { resetNotificationState } from "@store/slices/notificationSlice";
import { notify } from "@/shared/tostNotification/notify";

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

interface User {
  id: string;
  email: string;
  fullName?: string;
  role?: string;
  status?: "ACTIVE" | "INACTIVE";
  avatar?: string | null;
  [key: string]: any;
}

export interface TokenData {
  accessToken: string;
  refreshToken?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  lastActivity: number | null;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthResponse {
  user: User;
  tokens: TokenData;
}

interface DeviceTokenPayload {
  device_token: string;
  device_type: "w";
}

/* -------------------------------------------------------------------------- */
/*                               Initial State                                */
/* -------------------------------------------------------------------------- */

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  lastActivity: null,
};

/* -------------------------------------------------------------------------- */
/*                               Async Thunks                                 */
/* -------------------------------------------------------------------------- */

/**
 * Login user
 * POST: /auth/login
 */
export const loginUser = createAsyncThunk<AuthResponse, LoginCredentials>(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await httpService.post(
        API_CONFIG.path.login,
        credentials,
        {},
        {
          isAccessTokenRequire: false,
          customMessages: {
            success: "Login successful",
            error: "Login failed",
          },
        }
      );
      notify(response.message, "success");
      return response.data;
    } catch (error: any) {
      notify(error.message, "error");
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

/**
 * Logout user
 * POST: /auth/logout
 */
export const logoutUser = createAsyncThunk<void, void, { state: any; rejectValue: string }>(
  "auth/logout",
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      dispatch(resetNotificationState());
      const state = getState();
      const deviceToken = state.auth.user?.device_token;
      const response = await httpService.post(
        API_CONFIG.path.logout,
        {},
        {
          device_token: deviceToken,
        }
      );
      notify(response.message, "success");
      return response;
    } catch (error: any) {
      notify(error.message, "error");
      return rejectWithValue(error?.message || "Logout failed");
    }
  }
);

/**
 * Register user
 * POST: /auth/register
 */
export const registerUser = createAsyncThunk<any, any, { rejectValue: string }>(
  "auth/register",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await httpService.post(
        API_CONFIG.path.signup,
        userData,
        {},
        {
          isAccessTokenRequire: false,
        }
      );
      notify(response.message, "success");
      return response.data;
    } catch (error: any) {
      notify(error.message, "error");
      return rejectWithValue(error?.message);
    }
  }
);

/**
 * Update user profile
 * PUT: /auth/profile
 */
export const updateUserProfile = createAsyncThunk<User, Partial<User>>(
  "auth/updateProfile",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await httpService.put(API_CONFIG.path.profile_update, userData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Profile update failed");
    }
  }
);

/**
 * Forgot password
 */
export const forgotPassword = createAsyncThunk<string, { email: string }>(
  "auth/forgotPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await httpService.post(
        API_CONFIG.path.forgotPasswordSendMail,
        { email },
        {},
        { isAccessTokenRequire: false }
      );
      return response?.message || 'Password reset link has been sent to your email';
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to send reset email");
    }
  }
);

/**
 * Fetch Profile
 */

export const fetchProfile = createAsyncThunk<User>(
  "auth/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await httpService.get(API_CONFIG.path.get_profile);
      const data = response.data;
      const { device_token, ...rest } = data;
      return {
        avatar: data.file_url,
        ...rest,
      };
      // return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch profile");
    }
  }
);

export const uploadProfileImage = createAsyncThunk<any, File>(
  "auth/uploadProfileImage",
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await httpService.post(API_CONFIG.path.image_upload, formData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to upload image");
    }
  }
);

export const updateProfile = createAsyncThunk<User, any>(
  "auth/updateProfile",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await httpService.put(API_CONFIG.path.profile_update, {
        ...payload,
        is_profile_update: true,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update profile");
    }
  }
);

export const changePassword = createAsyncThunk<void, any>(
  "auth/changePassword",
  async (payload, { rejectWithValue }) => {
    try {
      await httpService.put(API_CONFIG.path.profile_update, payload);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to change password");
    }
  }
);

/**
 * Update Device Token
 * PUT: /auth/update
 */

export const updateDeviceToken = createAsyncThunk<
  void,
  DeviceTokenPayload,
  { rejectValue: string }
>("auth/updateDeviceToken", async (payload, { rejectWithValue }) => {
  try {
    await httpService.put(API_CONFIG.path.updateDeviceToken, payload);
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || "Failed to update device token");
  }
});

/* -------------------------------------------------------------------------- */
/*                                  Slice                                     */
/* -------------------------------------------------------------------------- */

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.error = null;
      state.lastActivity = Date.now();
    },

    updateToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.lastActivity = Date.now();
    },

    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },

    clearCredentials: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.lastActivity = null;
    },

    setAuthError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    clearAuthError: (state) => {
      state.error = null;
    },

    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    updateLastActivity: (state) => {
      state.lastActivity = Date.now();
    },

    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },

    setDeviceToken(state, action: PayloadAction<string>) {
      if (state.user) {
        if (state.user) {
          state.user.device_token = action.payload;
        }
      }
    },

    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.tokens.accessToken;
        state.isAuthenticated = true;
        state.lastActivity = Date.now();
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Logout (always clear state)
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }))
      .addCase(logoutUser.rejected, () => ({ ...initialState }))

      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Profile
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.user = action.payload;
        state.lastActivity = Date.now();
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Upload Profile Image
    builder
      .addCase(uploadProfileImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadProfileImage.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        if (state.user) {
          state.user.avatar = action.payload.presignedUrl;
        }
      })
      .addCase(uploadProfileImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Profile
    builder
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        if (state.user) {
          state.user.id = action.payload.id;
          state.user.fullName = action.payload.fullName;
          state.user.email = action.payload.email;
          state.user.mobile = action.payload.mobile;
          state.user.role = action.payload.role;
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Change Password
    builder
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

/* -------------------------------------------------------------------------- */
/*                                  Exports                                   */
/* -------------------------------------------------------------------------- */

export const {
  setCredentials,
  updateToken,
  updateUser,
  clearCredentials,
  setAuthError,
  clearAuthError,
  setAuthLoading,
  updateLastActivity,
  setAuthenticated,
  setDeviceToken,
  clearError,
} = authSlice.actions;

export default authSlice.reducer;
